// ManagementTable.jsx
import React, { useMemo, useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { MdEdit } from "react-icons/md";
import { FiSearch } from "react-icons/fi";
import Card from "components/card";
import EditUserModal from "../components/functions/EditUserModal";

const ManagementTable = ({ columnsData, tableData }) => {
  const [editUser, setEditUser] = useState(null);

  /* ──────────────────────  React‑Table setup  ────────────────────── */
  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    /* table + filter helpers */
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    /* pagination helpers */
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    /* state */
    state: { pageIndex, globalFilter },
  } = tableInstance;

  /* ──────────────────────  UI handlers  ────────────────────── */
  const handleEditUser = (user) => setEditUser(user);
  const closeEditModal = () => setEditUser(null);

  const defaultImageUrl =
    "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";

  /* ──────────────────────────────  JSX  ───────────────────────────── */
  return (
    <Card extra="w-full px-9 py-3 h-full">
      {/* Header */}
      <div className="mt-2 flex w-full items-center justify-between px-1">
        <h4 className="font-dm text-xl font-bold text-navy-700 dark:text-white">
          User Management
        </h4>
      </div>

      {/* Search bar */}
      <div className="flex w-3/4 items-center rounded-[10px] bg-white p-[8px] pt-[18px] shadow-2xl shadow-white dark:!bg-navy-800 dark:shadow-none md:ml-3 md:w-[400px]">
        <div className="flex h-9 w-[400px] flex-grow items-center rounded-[10px] bg-lightPrimary text-sm text-gray-600 dark:!bg-navy-900 dark:text-white">
          <FiSearch className="mx-2 h-4 w-4 !text-gray-700 dark:!text-white" />
          <input
            type="text"
            placeholder="Search…"
            value={globalFilter || ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="block h-full w-full rounded-full bg-lightPrimary text-sm text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
          />
        </div>
      </div>

      {/* Table */}
      <div className="mt-[25px] overflow-x-scroll 2xl:overflow-x-hidden">
        <table {...getTableProps()} className="w-full font-dm text-navy-700">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                key={headerGroup.id}
                className="items-center border-b border-gray-200 text-base dark:border-white/10"
              >
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="pb-[15px] pl-[5px] text-start text-sm font-bold uppercase tracking-wide text-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700"
                  >
                    <div className="flex items-center gap-1">
                      {column.render("Header")}
                      {column.isSorted && (
                        <span className="text-xs">
                          {column.isSortedDesc ? ' ↓' : ' ↑'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  className="items-center border-b dark:border-white/5"
                >
                  {row.cells.map((cell) => {
                    let content;

                    /* Name (with avatar) */
                    if (cell.column.Header === "Name") {
                      const imageUrl = cell.value[2] || defaultImageUrl;
                      content = (
                        <div className="flex items-center gap-2 pr-16 sm:min-w-[120px] md:min-w-[200px] lg:pr-0">
                          <img
                            src={imageUrl}
                            alt=""
                            className="h-9 w-9 rounded-xl"
                          />
                          <div className="flex flex-col">
                            <p className="text-sm font-bold text-navy-700 dark:text-white">
                              {cell.value[0]}
                            </p>
                            <p className="text-sm font-medium text-gray-600">
                              {cell.value[1]}
                            </p>
                          </div>
                        </div>
                      );
                    }

                    /* Phone */
                    else if (cell.column.Header === "Phone") {
                      const isMissing = !cell.value;
                      content = (
                        <div
                          className={`text-sm font-medium sm:min-w-[120px] ${
                            isMissing
                              ? "text-red-500 italic"
                              : "text-navy-700 dark:text-white"
                          }`}
                        >
                          {isMissing ? "No Phone number" : cell.value}
                        </div>
                      );
                    }

                    /* Team / Role / School */
                    else if (
                      ["Team", "Role", "School"].includes(cell.column.Header)
                    ) {
                      content = (
                        <div className="text-sm font-bold dark:text-white sm:min-w-[100px]">
                          {cell.value}
                        </div>
                      );
                    }

                    /* Member Since - handled by custom Cell renderer */
                    else if (cell.column.Header === "Member Since") {
                      const formattedDate = cell.value ? new Date(cell.value).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }) : 'N/A';
                      content = (
                        <div className="text-sm font-bold dark:text-white sm:min-w-[100px]">
                          {formattedDate}
                        </div>
                      );
                    }

                    /* Status badge */
                    else if (cell.column.Header === "Status") {
                      const offline = cell.value.toLowerCase() === "offline";
                      content = (
                        <div className="sm:min-w-[120px] md:min-w-[150px]">
                          <div
                            className={`flex w-[95px] items-center justify-center rounded-[10px] py-1.5 text-sm font-bold uppercase ${
                              offline
                                ? "bg-red-100 text-red-500 dark:bg-red-50"
                                : "bg-green-100 text-green-500 dark:bg-green-50"
                            }`}
                          >
                            {cell.value}
                          </div>
                        </div>
                      );
                    }

                    /* Edit button */
                    else if (cell.column.Header === "") {
                      content = (
                        <button
                          onClick={() => handleEditUser(row.original)}
                          className="text-xl text-gray-600"
                        >
                          <MdEdit />
                        </button>
                      );
                    }

                    return (
                      <td
                        {...cell.getCellProps()}
                        key={cell.column.id}
                        className="py-[14px] pl-2 lg:pr-2"
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <button
          onClick={previousPage}
          disabled={!canPreviousPage}
          className="rounded px-4 py-2 bg-orange-500 text-white disabled:bg-gray-300"
        >
          Previous
        </button>
        <span className="text-navy-700 dark:text-white">
          Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
        </span>
        <button
          onClick={nextPage}
          disabled={!canNextPage}
          className="rounded px-4 py-2 bg-orange-500 text-white disabled:bg-gray-300"
        >
          Next
        </button>
      </div>

      {/* Edit modal */}
      {editUser && <EditUserModal user={editUser} onClose={closeEditModal} />}
    </Card>
  );
};

export default ManagementTable;
